/**
 * Analytics tracking utility
 * Allows users to opt-out of analytics tracking via LocalStorage
 */

export const isAnalyticsEnabled = (): boolean => {
  if (typeof window === 'undefined') return true;
  return localStorage.getItem('analytics_disabled') !== 'true';
};

export const disableAnalytics = (): void => {
  localStorage.setItem('analytics_disabled', 'true');
  console.log('📊 Analytics wurde deaktiviert');
};

export const enableAnalytics = (): void => {
  localStorage.removeItem('analytics_disabled');
  console.log('📊 Analytics wurde aktiviert');
};

export const toggleAnalytics = (): void => {
  if (isAnalyticsEnabled()) {
    disableAnalytics();
  } else {
    enableAnalytics();
  }
};

export const getAnalyticsStatus = (): { enabled: boolean; message: string } => {
  const enabled = isAnalyticsEnabled();
  return {
    enabled,
    message: enabled 
      ? '📊 Analytics ist aktiviert' 
      : '🚫 Analytics ist deaktiviert'
  };
};

// Get or create session ID
const getSessionId = (): string => {
  if (typeof window === 'undefined') return '';
  
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Detect device type from user agent
const getDeviceType = (): string => {
  if (typeof window === 'undefined') return 'unknown';
  
  const ua = navigator.userAgent.toLowerCase();
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/mobile|iphone|ipod|android|blackberry|opera mini|windows phone/i.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

// Parse acquisition source from referrer
const getAcquisitionSource = (): string => {
  if (typeof window === 'undefined') return 'direct';
  
  const referrer = document.referrer.toLowerCase();
  if (!referrer) return 'direct';
  
  if (referrer.includes('google')) return 'organic';
  if (referrer.includes('facebook') || referrer.includes('twitter') || referrer.includes('linkedin')) return 'social';
  if (referrer.includes(window.location.hostname)) return 'internal';
  return 'referral';
};

// Enhanced Event Tracking: sends to Plausible, gtag, and our own database
export const trackEvent = async (
  name: string,
  params?: Record<string, any>
): Promise<void> => {
  try {
    if (!isAnalyticsEnabled()) return;

    // Track to external services
    // @ts-ignore
    if (typeof window !== 'undefined' && typeof window.plausible === 'function') {
      // @ts-ignore
      window.plausible(name, { props: params });
    }
    // @ts-ignore
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      // @ts-ignore
      window.gtag('event', name, params || {});
    }

    // Track to our own database via edge function
    if (typeof window !== 'undefined') {
      const eventData = {
        event_name: name,
        event_category: params?.category || 'general',
        page_path: window.location.pathname,
        session_id: getSessionId(),
        referrer: document.referrer || null,
        properties: {
          ...params,
          device_type: getDeviceType(),
          acquisition_source: getAcquisitionSource(),
          first_page: sessionStorage.getItem('first_page') || window.location.pathname,
        },
      };

      // Store first page for this session
      if (!sessionStorage.getItem('first_page')) {
        sessionStorage.setItem('first_page', window.location.pathname);
      }

      // Fire and forget - don't wait for response
      fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-analytics-event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify(eventData),
      }).catch(() => {
        // Fail silently
      });
    }
  } catch (e) {
    // fail silent
  }
};
